import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccions, useActivacio, useDestinataris } from '@/api/activacions';
import { descarregaInformeActivacio, type FormatInforme } from '@/api/informes';
import { Icona } from '@/components/Icona';
import { useFormat, useIdioma } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { BadgeEstat, BadgeEstatDestinatari, BadgeTipus, Buit } from '@/pages/operacio/comu';
import { textAccio } from '@/pages/operacio/suport';
import { useMe } from '@/permisos/MeContext';
import { tePermis } from '@/permisos/permisos';
import { colorEvent, useTemps } from './suport';
import './informes.css';

const NS_OPERACIO = 'operacio:';

export function W43InformeActivacio() {
  const { t } = useTranslation(['informes', 'comu', 'operacio']);
  const f = useFormat();
  const temps = useTemps();
  const idioma = useIdioma();
  const navigate = useNavigate();
  const { me } = useMe();
  const id = Number(useParams().id);
  const activacio = useActivacio(id);
  const destinataris = useDestinataris(id);
  const accions = useAccions(id);
  const [error, setError] = useState(false);
  const [generat] = useState(() => new Date());
  const a = activacio.data;
  const potAuditar = tePermis(me, 'cai.auditoria', 'CONSULTAR');

  const descarrega = async (format: FormatInforme) => {
    if (!a) return;
    setError(false);
    try {
      await descarregaInformeActivacio(a.id, a.codi, format, idioma);
    } catch {
      setError(true);
    }
  };

  const dest = destinataris.data ?? [];
  const confirmats = dest.filter((d) => d.confirmatAt).length;
  const cronologia = [...(accions.data ?? [])].sort((x, y) => x.at.localeCompare(y.at));
  const obertura = a ? (a.iniciIncident ?? a.createdAt) : null;
  const tancada = a?.tancament?.at ?? null;
  const durada =
    obertura && tancada
      ? (new Date(tancada).getTime() - new Date(obertura).getTime()) / 1000
      : null;
  const nomMe = me ? `${me.nom} ${me.cognoms ?? ''}`.trim() : '—';

  return (
    <section className="pantalla" data-screen="W-43">
      <Toolbar
        titol={a ? t('w43.titol', { codi: a.codi }) : t('titols.informeActivacio')}
        enrere="/analisi/informes"
      >
        {potAuditar && a ? (
          <button
            type="button"
            className="btn btn--s"
            onClick={() => navigate(`/analisi/tracabilitat/${a.id}`)}
          >
            <Icona nom="link" />
            {t('nav.tracabilitat')}
          </button>
        ) : null}
        {(['CSV', 'XLSX'] as FormatInforme[]).map((fo) => (
          <button
            key={fo}
            type="button"
            className="btn btn--s"
            disabled={!a}
            onClick={() => void descarrega(fo)}
          >
            <Icona nom="doc-export" />
            {t('w43.descarrega', { format: fo })}
          </button>
        ))}
      </Toolbar>
      <div className="content stack">
        {error ? (
          <div className="avis-banda" role="alert">
            <Icona nom="exclamationmark-triangle" />
            {t('w42.errors.descarrega')}
          </div>
        ) : null}
        {activacio.isError ? (
          <ErrorCarrega onReintenta={() => void activacio.refetch()} />
        ) : !a ? (
          <Buit icona="doc-text">{t('comu.carregant')}</Buit>
        ) : (
          <div className="grid grid-sidebar">
            <article className="panel document" data-testid="informe">
              <div className="document__cap">
                <div className="document__logo">
                  <Icona nom="shield-check" />
                </div>
                <div>
                  <div className="t-headline">{t('comu:app.nom')}</div>
                  <div className="t-caption-2 dim">{t('w43.subtitol')}</div>
                </div>
                <div className="spacer" />
                <BadgeTipus tipus={a.tipus} critica={a.critica} />
                <BadgeEstat estat={a.estat} />
              </div>
              <h2 className="document__titol">{a.titol}</h2>
              <div className="t-footnote dim mt-4">
                {[a.codi, a.servei, a.zona ?? a.zonaText].filter(Boolean).join(' · ')}
              </div>
              <div className="document__xifres">
                <div className="kv">
                  <span className="kv__k">{t('w43.oberta')}</span>
                  <span className="kv__v">{obertura ? f.dataHora(obertura) : '—'}</span>
                </div>
                <div className="kv">
                  <span className="kv__k">{t('w43.tancada')}</span>
                  <span className="kv__v">{tancada ? f.dataHora(tancada) : '—'}</span>
                </div>
                <div className="kv">
                  <span className="kv__k">{t('w43.durada')}</span>
                  <span className="kv__v">{durada != null ? temps(durada) : '—'}</span>
                </div>
              </div>

              <div className="document__seccio">{t('w43.dades')}</div>
              <div>
                <div className="kv">
                  <span className="kv__k">{t('comu.area')}</span>
                  <span className="kv__v">
                    {[a.departament, a.area].filter(Boolean).join(' · ') || '—'}
                  </span>
                </div>
                <div className="kv">
                  <span className="kv__k">{t('w43.activador')}</span>
                  <span className="kv__v">{a.activador ?? '—'}</span>
                </div>
                <div className="kv">
                  <span className="kv__k">{t('w43.canals')}</span>
                  <span className="kv__v">
                    {a.canals.map((c) => t(`comu:canal.${c}`)).join(', ') || '—'}
                  </span>
                </div>
                {a.itcau?.ref ? (
                  <div className="kv">
                    <span className="kv__k">{t('w43.itcau')}</span>
                    <span className="kv__v mono">{a.itcau.ref}</span>
                  </div>
                ) : null}
                <div className="kv kv--bloc">
                  <span className="kv__k">{t('w43.descripcio')}</span>
                  <div className="kv__v">{a.descripcio ?? '—'}</div>
                </div>
                {a.provesRealitzades ? (
                  <div className="kv kv--bloc">
                    <span className="kv__k">{t('w43.proves')}</span>
                    <div className="kv__v">{a.provesRealitzades}</div>
                  </div>
                ) : null}
              </div>

              <div className="document__seccio">{t('w43.cronologia')}</div>
              {cronologia.length === 0 ? (
                <Buit icona="clock">{t('comu.senseDades')}</Buit>
              ) : (
                <ol className="timeline">
                  {cronologia.map((ac) => (
                    <li
                      key={ac.id}
                      className={`tl-item tl-item--${colorEvent(`ACCIO_${ac.tipus}`)}`}
                    >
                      <div className="tl-time">{f.dataHora(ac.at)}</div>
                      <div className="tl-title">
                        {t(`tipusAccio.${ac.tipus}`, { defaultValue: ac.tipus })}
                      </div>
                      <div className="tl-text">{textAccio(ac, t, NS_OPERACIO)}</div>
                    </li>
                  ))}
                </ol>
              )}

              <div className="document__seccio">
                {t('w43.confirmats', { confirmats, total: dest.length })}
              </div>
              <table className="taula">
                <thead>
                  <tr>
                    <th>{t('w43.persona')}</th>
                    <th>{t('w43.bloc')}</th>
                    <th>{t('w43.lliurat')}</th>
                    <th>{t('w43.llegit')}</th>
                    <th>{t('w43.confirmat')}</th>
                    <th>{t('w43.estat')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dest.map((d) => (
                    <tr key={d.id}>
                      <td>{d.nom}</td>
                      <td>{t(`comu:blocDestinatari.${d.bloc}`)}</td>
                      <td className="mono">{d.lliuratAt ? f.hora(d.lliuratAt) : '—'}</td>
                      <td className="mono">{d.llegitAt ? f.hora(d.llegitAt) : '—'}</td>
                      <td className="mono">{d.confirmatAt ? f.hora(d.confirmatAt) : '—'}</td>
                      <td>
                        <BadgeEstatDestinatari estat={d.estat} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {a.tancament?.motiu ? (
                <>
                  <div className="document__seccio">{t('w43.tancament')}</div>
                  <div className="kv">
                    <span className="kv__k">{t('w43.motiu')}</span>
                    <span className="kv__v">{t(`comu:estatActivacio.${a.tancament.motiu}`)}</span>
                  </div>
                  {a.tancament.comentari ? (
                    <div className="kv kv--bloc">
                      <span className="kv__k">{t('w43.comentari')}</span>
                      <div className="kv__v">{a.tancament.comentari}</div>
                    </div>
                  ) : null}
                </>
              ) : null}
              <div className="document__peu">{t('w43.peu')}</div>
            </article>
            <div className="stack">
              <div className="panel">
                <div className="panel__head">
                  <h3>{t('w43.metadades')}</h3>
                </div>
                <div className="panel__body">
                  <div className="kv">
                    <span className="kv__k">{t('w43.generatEl')}</span>
                    <span className="kv__v">{f.dataHora(generat)}</span>
                  </div>
                  <div className="kv">
                    <span className="kv__k">{t('w43.generatPer')}</span>
                    <span className="kv__v">{nomMe}</span>
                  </div>
                  <div className="kv">
                    <span className="kv__k">{t('w42.format')}</span>
                    <span className="kv__v">{t('w43.formatPdf')}</span>
                  </div>
                  <div className="kv">
                    <span className="kv__k">{t('w43.classificacio')}</span>
                    <span className="kv__v">{t('w43.usIntern')}</span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="btn btn--dark btn--block"
                onClick={() => void descarrega('PDF')}
              >
                <Icona nom="doc-export" />
                {t('w43.descarregaPdf')}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
